"use client";

import { WaitlistModal } from "@/components/waitlist-modal";

const oldWayEvidence = [
  {
    label: "Signal type",
    value: "Founder intuition & gut feel",
  },
  {
    label: "Data source",
    value: "Direct conversations",
  },
  {
    label: "Confidence basis",
    value: "Self-reported interest",
  },
  {
    label: "Scale check",
    value: "Back-of-envelope estimate",
  },
  {
    label: "Decision frame",
    value: '"Will this work?"',
  },
];

const holmesWayEvidence = [
  {
    label: "Signal type",
    value: "Observed behavior & spending",
  },
  {
    label: "Data source",
    value: "Community complaint patterns (Reddit, forums)",
  },
  {
    label: "Confidence basis",
    value: "Multi-source quote validation",
  },
  {
    label: "Scale check",
    value: "Post volume, frequency & severity",
  },
  {
    label: "Decision frame",
    value: '"Is demand already confirmed?"',
  },
];

export function BeforeAfterSection() {
  return (
    <section
      role="region"
      aria-label="Evidence quality comparison"
      className="border-t border-border bg-secondary/30 px-6 py-16 lg:px-8 lg:py-24"
    >
      <div className="mx-auto max-w-7xl">
        {/* Section label */}
        <div className="mb-10 flex items-center gap-3 lg:mb-12">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50">
            Research methodology
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="mx-auto max-w-2xl">
          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            <span className="text-balance">
              The evidence you anchor to shapes the conviction you get
            </span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground/85">
            The same product idea can look risky or promising. Holmes anchors to{" "}
            <em className="font-serif italic font-medium text-foreground/90">
              observed behavior
            </em>{" "}
            — not self-reported interest — so the conviction holds up under real
            market pressure.
          </p>
        </div>

        {/* Evidence comparison grid */}
        <div className="mx-auto mt-12 max-w-5xl rounded-xl border border-border bg-card lg:mt-14">
          {/* Column headers */}
          <div
            className="grid border-b border-border p-6 lg:grid-cols-2 lg:p-8"
            style={{ gridTemplateColumns: "1fr 1fr" }}
          >
            <div className="flex items-center gap-2 border-b border-border pb-4 lg:border-b-0 lg:border-r lg:pr-6">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
                The founder default
              </span>
            </div>
            <div className="flex items-center gap-2 pt-4 lg:pt-0 lg:pl-6">
              <span className="text-[10px] uppercase tracking-widest text-accent/80">
                With Holmes
              </span>
            </div>
          </div>

          {/* Evidence rows */}
          <div className="divide-y divide-border">
            {oldWayEvidence.map((item, index) => {
              const holmesItem = holmesWayEvidence[index];
              return (
                <div
                  key={item.label}
                  className="grid p-6 lg:grid-cols-2 lg:p-8"
                  style={{ gridTemplateColumns: "1fr 1fr" }}
                >
                  <div className="pr-0 lg:pr-6">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50">
                      {item.label}
                    </p>
                    <p className="mt-2 text-sm leading-snug text-muted-foreground/80">
                      {item.value}
                    </p>
                  </div>
                  <div className="border-t border-border pt-4 lg:border-t-0 lg:border-l lg:pl-6 lg:pt-0">
                    <p className="text-[10px] uppercase tracking-widest text-accent/65">
                      {holmesItem.label}
                    </p>
                    <p className="mt-2 text-sm leading-snug font-medium text-foreground">
                      {holmesItem.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom note + CTA — selective invitation tone */}
        <div className="mx-auto mt-10 max-w-5xl">
          <div className="flex flex-col items-center gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-border" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40">
                First cohort — selective admission
              </span>
              <div className="h-px w-8 bg-border" />
            </div>
            <WaitlistModal
              triggerText="Apply for founding access"
              analyticsSource="before_after_section"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
